import type { CombinedError, OperationResult } from "urql";

/**
 * Loud-failure guardrail for GraphQL mutations.
 *
 * The app's mutations follow the relay-style payload convention: every
 * `*Payload` carries an `errors: string[]` field (see `BaseMutation`), and
 * `Maybe<Payload>` can also come back null to signal failure. On top of that,
 * urql surfaces transport/GraphQL errors on `result.error`. Historically some
 * call sites checked only the happy path (`errors.length === 0`) and silently
 * did nothing on failure — the "swallowed error" class this helper eliminates.
 *
 * `unwrapMutation` makes the loud path the path of least resistance: it returns
 * the mutation payload on success and throws a `MutationError` otherwise.
 * `runMutation` is the batteries-included companion that also surfaces the
 * failure to the user (and returns `null`) so a caller can write
 * `if (await runMutation(...)) { ...on success... }`.
 *
 * User-facing errors currently go through `window.alert`, which is the app's
 * existing error channel (e.g. `SelectionJourneyStage`). If a toast/notification
 * system is introduced, swap the body of `notifyError` in one place.
 */

export class MutationError extends Error {
  /** Gerund phrase describing the attempted action, e.g. "saving the candidate". */
  readonly action: string;
  /** Payload-level validation errors (the `errors: [...]` convention). */
  readonly payloadErrors: string[];
  /** urql transport / GraphQL error, when the failure was at that layer. */
  readonly transportError?: CombinedError;

  constructor(action: string, payloadErrors: string[], transportError?: CombinedError) {
    const detail =
      payloadErrors.length > 0
        ? payloadErrors.join("; ")
        : (transportError?.message ?? "unknown error");
    super(`Failed while ${action}: ${detail}`);
    this.name = "MutationError";
    this.action = action;
    this.payloadErrors = payloadErrors;
    this.transportError = transportError;
  }

  /** Message suitable for showing directly to a user. */
  get userMessage(): string {
    if (this.payloadErrors.length > 0) return this.payloadErrors.join("\n");
    return `Something went wrong while ${this.action}. Please try again.`;
  }
}

type MutationPayload = { errors?: readonly string[] | null } | null | undefined;

/**
 * Unwrap an urql mutation result, throwing `MutationError` on any failure
 * signal: a transport/GraphQL error, a null payload, or a non-empty payload
 * `errors` array. Returns the mutation's payload object on success.
 *
 * The single top-level payload is auto-extracted from `result.data`, so this
 * works for any mutation without naming its field.
 */
export function unwrapMutation<TData extends Record<string, unknown>>(
  result: OperationResult<TData>,
  action: string,
): NonNullable<TData[keyof TData]> {
  if (result.error) {
    throw new MutationError(action, [], result.error);
  }

  const data = result.data;
  if (data == null) {
    throw new MutationError(action, ["No response was returned by the server."]);
  }

  const payload = Object.values(data)[0] as MutationPayload;
  if (payload == null) {
    throw new MutationError(action, ["No response was returned by the server."]);
  }

  const errors = payload.errors;
  if (errors && errors.length > 0) {
    throw new MutationError(action, [...errors]);
  }

  return payload as NonNullable<TData[keyof TData]>;
}

/**
 * Surface a failure message to the user. Uses `window.alert` — the app's
 * existing user-facing error channel. This is the single seam to change if a
 * toast/notification system is adopted.
 */
export function notifyError(message: string): void {
  if (typeof window !== "undefined" && typeof window.alert === "function") {
    window.alert(message);
  }
}

/**
 * Log a caught failure and surface it to the user. Works for both
 * `MutationError` (uses its user-facing message) and any other thrown value
 * (e.g. a REST `fetch` rejection), so non-urql call sites can fail loudly too.
 */
export function reportError(error: unknown, action: string): void {
  const message =
    error instanceof MutationError
      ? error.userMessage
      : `Something went wrong while ${action}. Please try again.`;
  console.error(`[${action}] failed:`, error);
  notifyError(message);
}

/**
 * Run an urql mutation with loud failure built in: unwraps the result, and on
 * any failure reports it to the user and returns `null`. On success returns the
 * mutation payload.
 *
 *   const payload = await runMutation("importing this profile", () =>
 *     importProfile({ input }));
 *   if (payload) refresh();
 */
export async function runMutation<TData extends Record<string, unknown>>(
  action: string,
  execute: () => Promise<OperationResult<TData>>,
): Promise<NonNullable<TData[keyof TData]> | null> {
  try {
    return unwrapMutation(await execute(), action);
  } catch (error) {
    reportError(error, action);
    return null;
  }
}
